import {
    useCallback,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { SignupStep1 } from './components/SignupStep1';
import { SignupStep2 } from './components/SignupStep2';

import { baseRequest } from '@/lib/base';
import { passwordField } from '@/lib/passwordSchema';
import { toastApiError } from '@/lib/toast-error';

const step1Schema = z.object({
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: passwordField,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
});

type Step1Data = z.infer<typeof step1Schema>

const step2Schema = z.object({
    businessName: z.string().min(1, 'Business name is required'),
    ownerName: z.string()
        .min(1, 'Owner name is required')
        .regex(/^[A-Za-z][A-Za-z .'-]*$/, 'Owner name must contain only letters'),
    contactNo: z.string()
        .min(1, 'Contact number is required')
        .regex(/^\d{7,15}$/, 'Contact number must be 7-15 digits'),
    panVat: z.string()
        .min(1, 'PAN/VAT number is required')
        .regex(/^\d{9,15}$/, 'PAN/VAT must be 9-15 digits'),
    address: z.string().min(1, 'Address is required'),
    state: z.string().min(1, 'State is required'),
    city: z.string().min(1, 'City is required'),
    ward: z.string()
        .min(1, 'Ward number is required')
        .regex(/^\d+$/, 'Ward must be a number'),
    acceptPolicies: z.literal(true, {
        errorMap: () => ({ message: 'You must accept the company policies' }),
    }),
    acceptUpdates: z.boolean().optional(),
});

type Step2Data = z.infer<typeof step2Schema>

const checkEmailExists = async (email: string) => {
    const response = await baseRequest({
        url: '/system/auth/check-email-exists/',
        method: 'POST',
        body: JSON.stringify({ email }),
        contentType: 'application/json',
    });
    return response;
};

const registerUser = async (payload: {
    email: string;
    password: string;
    confirm_password: string;
    business_name: string;
    username: string;
    address: string;
    state: string;
    city: string;
    ward: number;
    contact_number: string;
    business_type: string;
    vendor: { pan_number: string };
}) => {
    const response = await baseRequest({
        url: '/system/auth/register/',
        method: 'POST',
        body: JSON.stringify(payload),
        contentType: 'application/json',
    });
    return response;
};

export default function Signup() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);

    const step1Form = useForm<Step1Data>({
        resolver: zodResolver(step1Schema),
        defaultValues: step1Data ?? undefined,
    });

    const step2Form = useForm<Step2Data>({
        resolver: zodResolver(step2Schema),
        defaultValues: {
            acceptPolicies: false as unknown as true,
            acceptUpdates: false,
        },
    });

    const emailCheckMutation = useMutation({
        mutationFn: (email: string) => checkEmailExists(email),
        onSuccess: (response) => {
            if (response.ok && response.data?.exists === false) {
                setStep1Data(step1Form.getValues());
                setCurrentStep(2);
            } else if (response.data?.exists === true) {
                step1Form.setError('email', {
                    type: 'manual',
                    message: 'An account with this email already exists',
                });
            } else {
                toastApiError(response.data, 'Failed to verify email');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    const registerMutation = useMutation({
        mutationFn: registerUser,
        onSuccess: (response) => {
            if (response.ok) {
                toast.success(response.data?.detail || 'Registration successful. OTP sent to email.');
                navigate('/verify-otp', { state: { email: step1Data?.email, flow: 'registration' } });
            } else {
                toastApiError(response.data, 'Registration failed. Please try again.');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    const handleStep1Submit = useCallback((data: Step1Data) => {
        emailCheckMutation.mutate(data.email);
    }, [emailCheckMutation]);

    const handleStep2Submit = useCallback((data: Step2Data) => {
        if (!step1Data) return;
        registerMutation.mutate({
            email: step1Data.email,
            password: step1Data.password,
            confirm_password: step1Data.confirmPassword,
            business_name: data.businessName,
            username: data.ownerName,
            address: data.address,
            state: data.state,
            city: data.city,
            ward: Number(data.ward),
            contact_number: data.contactNo,
            business_type: 'general',
            vendor: { pan_number: data.panVat },
        });
    }, [step1Data, registerMutation]);

    const handleBack = useCallback(() => {
        setCurrentStep(1);
    }, []);

    if (currentStep === 1) {
        return (
            <SignupStep1
                form={step1Form}
                onSubmit={handleStep1Submit}
                emailCheckMutation={emailCheckMutation}
                isPasswordVisible={isPasswordVisible}
                setIsPasswordVisible={setIsPasswordVisible}
                isConfirmVisible={isConfirmVisible}
                setIsConfirmVisible={setIsConfirmVisible}
            />
        );
    }

    return (
        <SignupStep2
            form={step2Form}
            onSubmit={handleStep2Submit}
            registerMutation={registerMutation}
            handleBack={handleBack}
        />
    );
}
